// Custom API client for existing backend
export const backendUrl = __BACKEND_URL__ ?? "http://localhost:4000"
import i18n from "i18next"

// Import settings types
import type { StoreSettings, UpdateStoreSettingsRequest } from "../../types/settings"
import { getImageUrl, getAssetUrl } from "../image-utils"

// Custom FetchError class
export class FetchError extends Error {
  status: number
  statusText: string

  constructor(message: string, status: number = 500, statusText: string = "Internal Server Error") {
    super(message)
    this.name = "FetchError"
    this.status = status
    this.statusText = statusText
  }
}

// Reference list entities
export interface ProductTypeEntry {
  _id: string
  slug: string
  name: string
  en: { name: string }
  published?: boolean
}

export interface PackagingEntry {
  _id: string
  slug: string
  name: string
  en: { name: string }
  published?: boolean
}

export interface SizeEntry {
  _id: string
  slug: string
  name: string
  en: { name: string }
  published?: boolean
}

export interface TagEntry {
  _id: string
  slug: string
  name: string
  en: { name: string }
  published?: boolean
}

// Types for existing backend
export interface User {
  _id: string
  firstname: string
  lastname: string
  username: string
  email: string
  role: 'admin' | 'user' | 'superadmin'
  addresses: Array<{
    street: string
    city: string
    state?: string
    zip?: string
    country: string
    principal: boolean
  }>
  wishlist: string[]
  image?: string
  active: boolean
  createdAt: string
  updatedAt: string
}

export interface LoginResponse {
  _id: string
  firstname: string
  lastname: string
  username: string
  email: string
  role: 'admin' | 'user' | 'superadmin'
  addresses: User['addresses']
  wishlist: string[]
  image?: string
  active: boolean
  createdAt: string
  updatedAt: string
  accessToken: string
}

export interface LoginRequest {
  username: string
  password: string
}

export interface RegisterRequest {
  firstname: string
  lastname: string
  username: string
  email: string
  password: string
}

export interface ForgotPasswordRequest {
  email: string
}

export interface ResetPasswordRequest {
  token: string
  password: string
}

// Product types
export interface Product {
  _id: string
  slug: string
  name: string
  description: string
  subtitle?: string
  type?: ProductTypeEntry | string
  packaging?: PackagingEntry | string
  size?: SizeEntry | string
  reference?: string
  benefits?: string
  how_to_use?: string
  ingredients?: string
  is_giftcard: boolean
  discount_price?: string
  images: (string | { url: string })[]
  price: string
  thumbnail?: string
  ad_image?: string
  categories: Category[]
  range?: Range
  countInStock: number
  en: {
    name: string
    description: string
    subtitle?: string
    reference?: string
    benefits?: string
    how_to_use?: string
    ingredients?: string
  }
  published: boolean
  updatedBy?: User
  createdAt: string
  updatedAt: string
}

export interface CreateProductRequest {
  slug: string
  name: string
  description: string
  subtitle?: string
  type?: string
  packaging?: string
  size?: string
  reference?: string
  benefits?: string
  how_to_use?: string
  ingredients?: string
  is_giftcard?: boolean
  discount_price?: string
  images?: string[]
  price: string
  thumbnail?: string
  ad_image?: string
  categories?: string[]
  range?: string
  countInStock: number
  en: {
    name: string
    description: string
    subtitle?: string
    reference?: string
    benefits?: string
    how_to_use?: string
    ingredients?: string
  }
  published?: boolean
}

export type UpdateProductRequest = Partial<Omit<CreateProductRequest, 'en'>> & {
  en?: Partial<CreateProductRequest['en']>
}

// Range (Collection) types
export interface Range {
  _id: string
  slug: string
  name: string
  en: {
    name: string
  }
  products: Product[]
  published: boolean
  updatedBy?: User
  image?: string
  createdAt: string
  updatedAt: string
}

export interface CreateRangeRequest {
  slug: string
  name: string
  en: {
    name: string
  }
  published?: boolean
  image?: string
}

export interface UpdateRangeRequest extends Partial<CreateRangeRequest> { }

// Category types
export interface Category {
  _id: string
  slug: string
  name: string
  description?: string
  en: {
    name: string
  }
  published: boolean
  updatedBy?: User
  image?: string
  createdAt: string
  updatedAt: string
}

// Order types
export interface Order {
  _id: string
  orderNumber: string
  userId: User
  products: Array<{
    productId: Product
    quantity: number
  }>
  amount: number
  address: {
    street: string
    city: string
    state?: string
    zip?: string
    country: string
  }
  status: 'pending' | 'processing' | 'in_transit' | 'delivered' | 'cancelled'
  createdAt: string
  updatedAt: string
}

export interface UpdateOrderRequest {
  status?: Order['status']
}

// API Response types
export interface ApiListResponse<T> {
  data: T[]
  count: number
  offset: number
  limit: number
}

export interface ApiResponse<T> {
  data: T
}

// Banner types
export interface Banner {
  _id: string
  title?: string
  description?: string
  image: string
  link?: string
  buttonText?: string
  en?: {
    title?: string
    description?: string
    buttonText?: string
  }
  position: number
  published: boolean
  startAt?: string
  endAt?: string
  createdAt: string
  updatedAt: string
}

export interface CreateBannerRequest {
  title?: string
  description?: string
  image: string
  link?: string
  buttonText?: string
  en?: {
    title?: string
    description?: string
    buttonText?: string
  }
  position?: number
  published?: boolean
  startAt?: string
  endAt?: string
}

export type UpdateBannerRequest = Partial<CreateBannerRequest>

// Site configuration (homepage video, etc.)
export interface SiteConfig {
  _id?: string
  id?: string
  homepageVideoUrl?: string
  homepageVideoPoster?: string
  homepageVideoTitle?: string
  homepageVideoSubtitle?: string
  homepageVideoDescription?: string
  homepageVideoButtonText?: string
  homepageVideoButtonLink?: string
  homepageVideoActive?: boolean
  createdAt?: string
  updatedAt?: string
}

// Token management with expiration
class TokenManager {
  private static readonly TOKEN_KEY = 'dashboard_access_token'
  private static readonly USER_KEY = 'dashboard_user'
  private static readonly TOKEN_EXPIRY_KEY = 'dashboard_token_expiry'
  private static readonly TOKEN_LIFETIME_MS = 24 * 60 * 60 * 1000 // 24 heures

  static getToken(): string | null {
    if (typeof window === 'undefined') return null
    if (typeof localStorage === 'undefined') return null

    const token = localStorage.getItem(this.TOKEN_KEY)
    if (!token) return null

    // Vérifier si le token a expiré
    if (this.isTokenExpired()) {
      this.removeToken()
      return null
    }

    return token
  }

  static setToken(token: string): void {
    if (typeof window === 'undefined') return
    if (typeof localStorage === 'undefined') return

    const expiryTime = Date.now() + this.TOKEN_LIFETIME_MS
    localStorage.setItem(this.TOKEN_KEY, token)
    localStorage.setItem(this.TOKEN_EXPIRY_KEY, expiryTime.toString())
  }

  static removeToken(): void {
    if (typeof window === 'undefined') return
    if (typeof localStorage === 'undefined') return
    localStorage.removeItem(this.TOKEN_KEY)
    localStorage.removeItem(this.USER_KEY)
    localStorage.removeItem(this.TOKEN_EXPIRY_KEY)
  }

  static isTokenExpired(): boolean {
    if (typeof window === 'undefined') return true
    if (typeof localStorage === 'undefined') return true

    const expiryStr = localStorage.getItem(this.TOKEN_EXPIRY_KEY)
    if (!expiryStr) return true

    const expiryTime = Number.parseInt(expiryStr, 10)
    return Date.now() >= expiryTime
  }

  static getUser(): User | null {
    if (typeof window === 'undefined') return null
    if (typeof localStorage === 'undefined') return null

    // Vérifier si le token est expiré avant de retourner l'utilisateur
    if (this.isTokenExpired()) {
      this.removeToken()
      return null
    }

    const userStr = localStorage.getItem(this.USER_KEY)
    return userStr ? JSON.parse(userStr) : null
  }

  static setUser(user: User): void {
    if (typeof window === 'undefined') return
    if (typeof localStorage === 'undefined') return
    localStorage.setItem(this.USER_KEY, JSON.stringify(user))
  }

  static isAuthenticated(): boolean {
    return !!this.getToken() && !this.isTokenExpired()
  }

  static hasRole(role: 'admin' | 'superadmin'): boolean {
    const user = this.getUser()
    if (!user) return false
    return user.role === role || user.role === 'superadmin'
  }

  static isAdmin(): boolean {
    return this.hasRole('admin') || this.hasRole('superadmin')
  }

  static isSuperAdmin(): boolean {
    return this.hasRole('superadmin')
  }
}

// HTTP client with authentication
class ApiClient {
  private readonly baseUrl: string

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`
    const token = TokenManager.getToken()

    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { token: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    }

    try {
      const response = await fetch(url, config)

      if (!response.ok) {
        let errorMessage = 'An error occurred'

        try {
          const errorData = await response.json()
          errorMessage = errorData.message || errorMessage
        } catch {
          errorMessage = response.statusText || errorMessage
        }

        // Si erreur 401, déconnecter l'utilisateur automatiquement
        if (response.status === 401) {
          TokenManager.removeToken()
          // Rediriger vers la page de login si on n'y est pas déjà
          if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
            window.location.href = '/login'
          }
        }

        throw new FetchError(errorMessage, response.status, response.statusText)
      }

      // Handle empty responses
      const contentType = response.headers.get('content-type')
      if (contentType?.includes('application/json')) {
        return await response.json()
      }

      return {} as T
    } catch (error) {
      if (error instanceof FetchError) {
        throw error
      }
      throw new FetchError('Network error occurred')
    }
  }

  // Authentication methods
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await this.request<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    })

    // Store token and user data
    TokenManager.setToken(response.accessToken)
    const { accessToken, ...user } = response
    TokenManager.setUser(user as User)

    return response
  }

  async register(userData: RegisterRequest): Promise<LoginResponse> {
    const response = await this.request<LoginResponse>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    })

    // Store token and user data
    TokenManager.setToken(response.accessToken)
    const { accessToken, ...user } = response
    TokenManager.setUser(user as User)

    return response
  }

  async forgotPassword(data: ForgotPasswordRequest): Promise<{ message: string }> {
    return this.request<{ message: string }>('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async resetPassword(data: ResetPasswordRequest): Promise<{ message: string }> {
    return this.request<{ message: string }>('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  logout(): void {
    TokenManager.removeToken()
  }

  getCurrentUser(): User | null {
    return TokenManager.getUser()
  }

  isAuthenticated(): boolean {
    return TokenManager.isAuthenticated()
  }

  hasAdminAccess(): boolean {
    return TokenManager.isAdmin()
  }

  hasSuperAdminAccess(): boolean {
    return TokenManager.isSuperAdmin()
  }

  // Products API
  async getProducts(query?: {
    new?: boolean
    category?: string
    range?: string
    sort?: 'desc' | 'asc'
    pagination?: number
    page?: number
    q?: string  // Search query
    offset?: number
    limit?: number
    fields?: string
    order?: string
    status?: string[]
    collection_id?: string[]
    type_id?: string[]
    category_id?: string[]
  }): Promise<Product[]> {
    const params = new URLSearchParams()

    // Handle Medusa-style parameters
    if (query?.q) {
      // Search by name - backend doesn't support this yet, we'll filter client-side
      params.append('q', query.q)
    }

    if (query?.new) params.append('new', 'true')
    if (query?.category) params.append('category', query.category)
    if (query?.range) params.append('range', query.range)

    // Status filter
    if (query?.status && query.status.length > 0) {
      // Backend uses 'published' field
      params.append('published', query.status.includes('published') ? 'true' : 'false')
    }

    // Collection (Range) filter
    if (query?.collection_id && query.collection_id.length > 0) {
      params.append('range', query.collection_id[0])
    }

    // Category filter
    if (query?.category_id && query.category_id.length > 0) {
      params.append('category', query.category_id[0])
    }

    // Type filter
    if (query?.type_id && query.type_id.length > 0) {
      params.append('type', query.type_id[0])
    }

    // Sort
    if (query?.order) {
      // Medusa uses order like "-created_at" or "+title"
      const isDesc = query.order.startsWith('-')
      params.append('sort', isDesc ? 'desc' : 'asc')
    } else if (query?.sort) {
      params.append('sort', query.sort)
    } else {
      // Default to desc (newest first)
      params.append('sort', 'desc')
    }

    // Pagination - backend doesn't support offset/limit yet
    // We'll handle this client-side for now
    const pagination = query?.limit || query?.pagination || 20
    const page = query?.page || (query?.offset ? Math.floor(query.offset / pagination) + 1 : 1)

    const endpoint = `/api/products/all${params.toString() ? `?${params.toString()}` : ''}`
    let products = await this.request<Product[]>(endpoint)

    // Ensure products is always an array
    products = Array.isArray(products) ? products : []

    // Client-side search filter if query.q is provided
    if (query?.q) {
      const searchTerm = query.q.toLowerCase()
      products = products.filter(p =>
        p.name?.toLowerCase().includes(searchTerm) ||
        p.slug?.toLowerCase().includes(searchTerm) ||
        p.description?.toLowerCase().includes(searchTerm)
      )
    }

    return products
  }

  async getProduct(id: string): Promise<Product> {
    return this.request<Product>(`/api/products/find/${id}`)
  }

  async createProduct(data: CreateProductRequest): Promise<Product> {
    return this.request<Product>('/api/products', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateProduct(id: string, data: UpdateProductRequest): Promise<Product> {
    return this.request<Product>(`/api/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteProduct(id: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/api/products/${id}`, {
      method: 'DELETE',
    })
  }

  // Product reference lists API
  async getProductTypes(all?: boolean): Promise<ProductTypeEntry[]> {
    const endpoint = all ? '/api/product-types/all' : '/api/product-types'
    const items = await this.request<ProductTypeEntry[]>(endpoint)
    return Array.isArray(items) ? items : []
  }
  async getProductType(id: string): Promise<ProductTypeEntry> {
    return this.request<ProductTypeEntry>(`/api/product-types/find/${id}`)
  }
  async createProductType(data: { slug: string; name: string; en?: { name: string }; published?: boolean }): Promise<ProductTypeEntry> {
    return this.request<ProductTypeEntry>('/api/product-types', { method: 'POST', body: JSON.stringify(data) })
  }
  async updateProductType(id: string, data: Partial<{ slug: string; name: string; en: { name: string }; published: boolean }>): Promise<ProductTypeEntry> {
    return this.request<ProductTypeEntry>(`/api/product-types/${id}`, { method: 'PUT', body: JSON.stringify(data) })
  }
  async deleteProductType(id: string): Promise<{ message?: string }> {
    return this.request<{ message?: string }>(`/api/product-types/${id}`, { method: 'DELETE' })
  }

  async getPackaging(all?: boolean): Promise<PackagingEntry[]> {
    const endpoint = all ? '/api/packaging/all' : '/api/packaging'
    const items = await this.request<PackagingEntry[]>(endpoint)
    return Array.isArray(items) ? items : []
  }
  async getOnePackaging(id: string): Promise<PackagingEntry> {
    return this.request<PackagingEntry>(`/api/packaging/find/${id}`)
  }
  async createPackaging(data: { slug: string; name: string; en?: { name: string }; published?: boolean }): Promise<PackagingEntry> {
    return this.request<PackagingEntry>('/api/packaging', { method: 'POST', body: JSON.stringify(data) })
  }
  async updatePackaging(id: string, data: Partial<{ slug: string; name: string; en: { name: string }; published: boolean }>): Promise<PackagingEntry> {
    return this.request<PackagingEntry>(`/api/packaging/${id}`, { method: 'PUT', body: JSON.stringify(data) })
  }
  async deletePackaging(id: string): Promise<{ message?: string }> {
    return this.request<{ message?: string }>(`/api/packaging/${id}`, { method: 'DELETE' })
  }

  async getSizes(all?: boolean): Promise<SizeEntry[]> {
    const endpoint = all ? '/api/sizes/all' : '/api/sizes'
    const items = await this.request<SizeEntry[]>(endpoint)
    return Array.isArray(items) ? items : []
  }
  async getSize(id: string): Promise<SizeEntry> {
    return this.request<SizeEntry>(`/api/sizes/find/${id}`)
  }
  async createSize(data: { slug: string; name: string; en?: { name: string }; published?: boolean }): Promise<SizeEntry> {
    return this.request<SizeEntry>('/api/sizes', { method: 'POST', body: JSON.stringify(data) })
  }
  async updateSize(id: string, data: Partial<{ slug: string; name: string; en: { name: string }; published: boolean }>): Promise<SizeEntry> {
    return this.request<SizeEntry>(`/api/sizes/${id}`, { method: 'PUT', body: JSON.stringify(data) })
  }
  async deleteSize(id: string): Promise<{ message?: string }> {
    return this.request<{ message?: string }>(`/api/sizes/${id}`, { method: 'DELETE' })
  }

  // Tags API
  async getTags(all?: boolean): Promise<TagEntry[]> {
    const endpoint = all ? '/api/tags/all' : '/api/tags'
    const items = await this.request<TagEntry[]>(endpoint)
    return Array.isArray(items) ? items : []
  }
  async getTag(id: string): Promise<TagEntry> {
    return this.request<TagEntry>(`/api/tags/find/${id}`)
  }
  async createTag(data: { slug: string; name: string; en?: { name: string }; published?: boolean }): Promise<TagEntry> {
    return this.request<TagEntry>('/api/tags', { method: 'POST', body: JSON.stringify(data) })
  }
  async updateTag(id: string, data: Partial<{ slug: string; name: string; en: { name: string }; published: boolean }>): Promise<TagEntry> {
    return this.request<TagEntry>(`/api/tags/${id}`, { method: 'PUT', body: JSON.stringify(data) })
  }
  async deleteTag(id: string): Promise<{ message?: string }> {
    return this.request<{ message?: string }>(`/api/tags/${id}`, { method: 'DELETE' })
  }

  // Ranges (Collections) API
  async getRanges(query?: {
    new?: boolean
    sort?: 'desc' | 'asc'
    published?: boolean
  }): Promise<Range[]> {
    const params = new URLSearchParams()
    if (query?.new) params.append('new', 'true')
    if (query?.sort) params.append('sort', query.sort)
    if (query?.published !== undefined) params.append('published', query.published.toString())

    const endpoint = `/api/ranges/all${params.toString() ? `?${params.toString()}` : ''}`
    const ranges = await this.request<Range[]>(endpoint)

    // Ensure ranges is always an array and handle null/undefined values
    return Array.isArray(ranges) ? ranges : []
  }

  async getRange(id: string): Promise<Range> {
    return this.request<Range>(`/api/ranges/find/${id}`)
  }

  async createRange(data: CreateRangeRequest): Promise<Range> {
    return this.request<Range>('/api/ranges', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateRange(id: string, data: UpdateRangeRequest): Promise<Range> {
    return this.request<Range>(`/api/ranges/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteRange(id: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/api/ranges/${id}`, {
      method: 'DELETE',
    })
  }

  async addProductToRange(rangeId: string, productId: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/api/ranges/product/${productId}`, {
      method: 'PUT',
      body: JSON.stringify({ newRange: rangeId }),
    })
  }

  async removeProductFromRange(rangeId: string, productId: string): Promise<Range> {
    return this.request<Range>(`/api/ranges/remove/${rangeId}/product/${productId}`, {
      method: 'PUT',
    })
  }

  // Orders API
  async getOrders(query?: {
    delivered?: boolean
  }): Promise<Order[]> {
    const params = new URLSearchParams()
    if (query?.delivered !== undefined) params.append('delivered', query.delivered.toString())

    const endpoint = `/api/orders${params.toString() ? `?${params.toString()}` : ''}`
    const orders = await this.request<Order[]>(endpoint)

    // Ensure orders is always an array and handle null/undefined values
    return Array.isArray(orders) ? orders : []
  }

  async getOrder(id: string): Promise<Order> {
    // Accept either Mongo ObjectId or display order number
    if (!id) return null as unknown as Order
    const isObjectId = /^[a-fA-F0-9]{24}$/.test(id)
    try {
      if (isObjectId) {
        // Prefer direct backend route by Mongo _id
        const byId = await this.request<Order>(`/api/orders/find/order-by-id/${id}`)
        if (byId) return byId
        // Fallback: list and find locally
        const orders = await this.request<Order[]>(`/api/orders`)
        const found = Array.isArray(orders) ? orders.find((o) => o._id === id) : null
        return (found || null) as unknown as Order
      } else {
        // Treat id as display order number (without #)
        const display = id.replace(/^#/, '')
        const byDisplay = await this.request<Order>(`/api/orders/find/order/${display}`)
        if (byDisplay) return byDisplay
        // Fallback: list and find by orderNumber
        const orders = await this.request<Order[]>(`/api/orders`)
        const found = Array.isArray(orders) ? orders.find((o) => o.orderNumber === `#${display}`) : null
        return (found || null) as unknown as Order
      }
    } catch {
      return null as unknown as Order
    }
  }

  async updateOrder(id: string, data: UpdateOrderRequest): Promise<Order> {
    return this.request<Order>(`/api/orders/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteOrder(id: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/api/orders/${id}`, {
      method: 'DELETE',
    })
  }

  // Banners API
  async getBanners(query?: { published?: boolean }): Promise<Banner[]> {
    const params = new URLSearchParams()
    if (query?.published !== undefined) params.append('published', query.published.toString())
    const endpoint = `/api/banners${params.toString() ? `?${params.toString()}` : ''}`
    const banners = await this.request<Banner[]>(endpoint)
    return Array.isArray(banners) ? banners : []
  }

  async getBanner(id: string): Promise<Banner> {
    return this.request<Banner>(`/api/banners/find/${id}`)
  }

  async getAllBanners(): Promise<Banner[]> {
    const banners = await this.request<Banner[]>(`/api/banners/all`)
    return Array.isArray(banners) ? banners : []
  }

  async createBanner(data: CreateBannerRequest): Promise<Banner> {
    return this.request<Banner>('/api/banners', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateBanner(id: string, data: UpdateBannerRequest): Promise<Banner> {
    return this.request<Banner>(`/api/banners/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteBanner(id: string): Promise<{ message: string } | { success?: boolean }> {
    return this.request<{ message: string }>(`/api/banners/${id}`, {
      method: 'DELETE',
    })
  }

  // Users/Customers API
  async getUsers(query?: {
    new?: boolean
    admin?: boolean
  }): Promise<User[]> {
    const params = new URLSearchParams()
    if (query?.new) params.append('new', 'true')
    if (query?.admin) params.append('admin', 'true')

    const endpoint = `/api/users${params.toString() ? `?${params.toString()}` : ''}`
    const users = await this.request<User[]>(endpoint)

    // Ensure users is always an array and handle null/undefined values
    return Array.isArray(users) ? users : []
  }

  async getUser(id: string): Promise<User> {
    return this.request<User>(`/api/users/find/${id}`)
  }

  async updateUser(id: string, data: Partial<User>): Promise<User> {
    return this.request<User>(`/api/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteUser(id: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/api/users/${id}`, {
      method: 'DELETE',
    })
  }

  // Categories API
  async getCategories(): Promise<Category[]> {
    return this.request<Category[]>('/api/categories/all')
  }

  async getCategory(id: string): Promise<Category> {
    return this.request<Category>(`/api/categories/find/${id}`)
  }

  async createCategory(data: { name: string; slug: string; description?: string; published?: boolean }): Promise<Category> {
    return this.request<Category>('/api/categories', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async updateCategory(id: string, data: { name?: string; slug?: string; description?: string; published?: boolean }): Promise<Category> {
    return this.request<Category>(`/api/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  }

  async deleteCategory(id: string): Promise<void> {
    return this.request<void>(`/api/categories/${id}`, {
      method: 'DELETE'
    })
  }

  // Upload/Media API
  async uploadFiles(files: File[]): Promise<{ path: string; filename: string }[]> {
    const formData = new FormData()
    files.forEach(file => {
      formData.append('files', file)
    })

    const token = TokenManager.getToken()
    const response = await fetch(`${this.baseUrl}/api/upload`, {
      method: 'POST',
      headers: {
        ...(token && { token: `Bearer ${token}` }),
      },
      body: formData,
    })

    if (!response.ok) {
      let errorMessage = 'Upload failed'
      try {
        const errorData = await response.json()
        errorMessage = errorData.message || errorMessage
      } catch {
        errorMessage = response.statusText || errorMessage
      }
      throw new FetchError(errorMessage, response.status, response.statusText)
    }

    const result = await response.json()

    // Handle single file response (existing backend returns single object)
    if (result.path) {
      return [{ path: result.path, filename: files[0]?.name || 'unknown' }]
    }

    // Handle multiple files response (if backend is updated to support it)
    return Array.isArray(result) ? result : [result]
  }

  async saveUploadedFile(filename: string, folder: 'products' | 'ads' = 'products'): Promise<{ path: string; success: boolean }> {
    return this.request<{ path: string; success: boolean }>('/api/upload/save', {
      method: 'POST',
      body: JSON.stringify({ file: filename, folder }),
    })
  }

  async deleteUploadedFile(filename: string, folder: 'products' | 'ads' = 'products'): Promise<{ path: string; success: boolean }> {
    return this.request<{ path: string; success: boolean }>('/api/upload/delete', {
      method: 'POST',
      body: JSON.stringify({ file: filename, folder }),
    })
  }

  async revertUpload(path: string): Promise<{ path: string }> {
    return this.request<{ path: string }>('/api/upload/revert', {
      method: 'DELETE',
      body: JSON.stringify({ path }),
    })
  }

  // Site Config API
  async getSiteConfig(): Promise<SiteConfig> {
    return this.request<SiteConfig>('/api/settings/site-config')
  }

  async updateSiteConfig(data: Partial<SiteConfig>): Promise<SiteConfig> {
    return this.request<SiteConfig>('/api/settings/site-config', {
      method: 'PUT',
      body: JSON.stringify({
        homepageVideoUrl: data.homepageVideoUrl,
        homepageVideoPoster: data.homepageVideoPoster,
        homepageVideoTitle: data.homepageVideoTitle,
        homepageVideoSubtitle: data.homepageVideoSubtitle,
        homepageVideoDescription: data.homepageVideoDescription,
        homepageVideoButtonText: data.homepageVideoButtonText,
        homepageVideoButtonLink: data.homepageVideoButtonLink,
        homepageVideoActive: data.homepageVideoActive,
      }),
    })
  }

  // Store/Settings API
  async getStoreSettings(): Promise<StoreSettings> {
    try {
      const response = await this.request<StoreSettings[]>('/api/store')
      // API returns an array, get the first item
      if (Array.isArray(response) && response.length > 0) {
        const store = response[0]
        return {
          ...store,
          id: store._id || store.id || "store_01",
          store_name: store.store_name || "Laboratoire Luxe Cosmétique",
          phone_number: store.businessPhone || store.phone_number || "",
          email: store.email || "",
          phones: store.phones || [],
          others: store.others || [],
          address: store.address || {
            line1: "",
            city: "",
            postal_code: "",
            country: "France"
          },
          social_media: store.social_media || {},
          seo: store.seo || {},
          business_info: store.business_info || {},
          created_at: store.createdAt || store.created_at || new Date().toISOString(),
          updated_at: store.updatedAt || store.updated_at || new Date().toISOString()
        }
      }
      // Return default if no store found
      return {
        id: "store_01",
        store_name: "Laboratoire Luxe Cosmétique",
        phone_number: "",
        email: "",
        phones: [],
        others: [],
        address: {
          line1: "",
          city: "",
          postal_code: "",
          country: "France"
        },
        social_media: {},
        seo: {},
        business_info: {},
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
    } catch (error) {
      // If endpoint doesn't exist, return default settings
      if (error instanceof FetchError && error.status === 404) {
        return {
          id: "store_01",
          store_name: "Laboratoire Luxe Cosmétique",
          phone_number: "",
          email: "",
          phones: [],
          others: [],
          address: {
            line1: "",
            city: "",
            postal_code: "",
            country: "France"
          },
          social_media: {},
          seo: {},
          business_info: {},
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
      }
      throw error
    }
  }

  async updateStoreSettings(data: UpdateStoreSettingsRequest): Promise<StoreSettings> {
    try {
      // Get current store to get the ID
      const currentStore = await this.getStoreSettings()
      const storeId = currentStore._id || currentStore.id

      // Prepare update payload
      const updatePayload: any = {}
      if (data.email !== undefined) updatePayload.email = data.email
      if (data.businessPhone !== undefined) updatePayload.businessPhone = data.businessPhone
      if (data.phone_number !== undefined) updatePayload.businessPhone = data.phone_number

      // Handle phones and others robustly to match backend expectations
      // If phones is provided as an array of strings, some backends define `phones` as an embedded doc/array of objects,
      // which causes a CastError. In that case, merge string phones into `others` and omit `phones` from the payload.
      const providedOthers = Array.isArray(data.others) ? data.others : undefined
      if (Array.isArray(data.phones)) {
        const hasOnlyStrings = data.phones.every((p) => typeof p === "string")
        if (hasOnlyStrings) {
          // Merge into others to keep free-form text, avoid embedded cast on backend
          updatePayload.others = [
            ...((providedOthers ?? []) as string[]),
            ...((data.phones as string[]) ?? []),
          ]
        } else {
          // Assume objects already match backend shape
          updatePayload.phones = data.phones
          if (providedOthers !== undefined) updatePayload.others = providedOthers
        }
      } else if (providedOthers !== undefined) {
        updatePayload.others = providedOthers
      }

      // Social media: only send non-empty values
      if (data.social_media !== undefined) {
        const cleaned = Object.fromEntries(
          Object.entries(data.social_media).filter(([, v]) =>
            typeof v === "string" && v.trim().length > 0
          )
        )
        updatePayload.social_media = cleaned
      }

      const response = await this.request<StoreSettings>(`/api/store/${storeId}`, {
        method: 'PUT',
        body: JSON.stringify(updatePayload),
      })

      return {
        ...response,
        id: response._id || response.id || storeId,
        phone_number: response.businessPhone || response.phone_number || "",
        phones: response.phones || [],
        others: response.others || [],
        created_at: response.createdAt || response.created_at || currentStore.created_at,
        updated_at: response.updatedAt || response.updated_at || new Date().toISOString()
      }
    } catch (error) {
      // If endpoint doesn't exist, simulate update by returning merged data
      if (error instanceof FetchError && (error.status === 404 || error.status === 405)) {
        try {
          const currentSettings = await this.getStoreSettings()
          return {
            ...currentSettings,
            ...data,
            address: {
              ...currentSettings.address,
              ...data.address
            },
            phones: data.phones !== undefined ? data.phones : (currentSettings.phones || []),
            others: data.others !== undefined ? data.others : (currentSettings.others || []),
            social_media: data.social_media ? { ...currentSettings.social_media, ...data.social_media } : currentSettings.social_media,
            updated_at: new Date().toISOString()
          }
        } catch {
          // If getStoreSettings also fails, return default with updates
          return {
            id: "store_01",
            store_name: data.store_name || "Laboratoire Luxe Cosmétique",
            phone_number: data.phone_number || data.businessPhone || "",
            email: data.email || "contact@laboratoireluxecosmetique.com",
            phones: data.phones || [],
            others: data.others || [],
            address: {
              line1: data.address?.line1 || "",
              line2: data.address?.line2 || "",
              city: data.address?.city || "",
              postal_code: data.address?.postal_code || "",
              country: data.address?.country || "France"
            },
            social_media: data.social_media || {},
            seo: data.seo || {},
            business_info: data.business_info || {},
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }
        }
      }
      throw error
    }
  }
}

// Create API client instance
const apiClient = new ApiClient(backendUrl)

// Custom SDK compatible with existing hooks
export const sdk = {
  auth: {
    login: async (provider: string, method: string, payload: { email: string; password: string }) => {
      // Convert email-based login to username-based for existing backend
      // For now, we'll assume username = email, but this can be adjusted
      const response = await apiClient.login({
        username: payload.email, // Using email as username for compatibility
        password: payload.password,
      })
      return response.accessToken
    },
    register: async (provider: string, method: string, payload: RegisterRequest) => {
      const response = await apiClient.register(payload)
      return response.accessToken
    },
    resetPassword: async (provider: string, method: string, payload: { identifier: string }) => {
      await apiClient.forgotPassword({ email: payload.identifier })
    },
    updateProvider: async (provider: string, method: string, payload: ResetPasswordRequest, token: string) => {
      await apiClient.resetPassword({ token, password: payload.password })
    },
    logout: async () => {
      apiClient.logout()
    },
  },
  admin: {
    banner: {
      list: async () => {
        const banners = await apiClient.getAllBanners()
        return { banners }
      },
      retrieve: async (id: string) => {
        const banner = await apiClient.getBanner(id)
        return { banner }
      },
      create: async (payload: CreateBannerRequest) => {
        const banner = await apiClient.createBanner(payload)
        return { banner }
      },
      update: async (id: string, payload: UpdateBannerRequest) => {
        const banner = await apiClient.updateBanner(id, payload)
        return { banner }
      },
      delete: async (id: string) => {
        await apiClient.deleteBanner(id)
        return { success: true }
      },
    },
    product: {
      list: async (query?: any) => {
        const allProducts = await apiClient.getProducts(query)

        // Trier par date de création décroissante (plus récent en premier) ou selon order
        let sortedProducts = [...allProducts]
        if (query?.order) {
          const field = query.order.replace(/^[+-]/, '')
          const isDesc = query.order.startsWith('-')
          sortedProducts.sort((a, b) => {
            let aVal, bVal
            if (field === 'created_at') {
              aVal = new Date(a.createdAt).getTime()
              bVal = new Date(b.createdAt).getTime()
            } else if (field === 'updated_at') {
              aVal = new Date(a.updatedAt).getTime()
              bVal = new Date(b.updatedAt).getTime()
            } else if (field === 'title') {
              aVal = a.name?.toLowerCase() || ''
              bVal = b.name?.toLowerCase() || ''
            } else {
              aVal = new Date(a.createdAt).getTime()
              bVal = new Date(b.createdAt).getTime()
            }
            return isDesc ? (bVal > aVal ? 1 : -1) : (aVal > bVal ? 1 : -1)
          })
        } else {
          sortedProducts.sort((a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          )
        }

        // Pagination côté client
        const limit = query?.limit || 20
        const offset = query?.offset || 0
        const paginatedProducts = sortedProducts.slice(offset, offset + limit)

        return {
          products: paginatedProducts.map(p => ({
            ...p,
            id: p._id,
            // Localized title depending on dashboard language
            title: (() => {
              const lang = (i18n?.language || 'fr').toLowerCase()
              if (lang.startsWith('fr')) return p.name || p.en?.name || p.slug || 'Untitled Product'
              return p.en?.name || p.name || p.slug || 'Untitled Product'
            })(),
            handle: p.slug || 'untitled-product',
            // Single collection object for tables using 'collection'
            collection: p.range ? { id: p.range._id, title: p.range.name, handle: p.range.slug } : undefined,
            // Optional typed refs for sidebar display
            type: (p as any).type && typeof (p as any).type === 'object' ? { id: (p as any).type._id, value: (p as any).type.name, handle: (p as any).type.slug } : undefined,
            packaging: (p as any).packaging && typeof (p as any).packaging === 'object' ? { id: (p as any).packaging._id, value: (p as any).packaging.name, handle: (p as any).packaging.slug } : undefined,
            size: (p as any).size && typeof (p as any).size === 'object' ? { id: (p as any).size._id, value: (p as any).size.name, handle: (p as any).size.slug } : undefined,
            collection_id: p.range?._id,
            type_id: (p as any).type?._id || (typeof (p as any).type === 'string' ? (p as any).type : undefined),
            packaging_id: (p as any).packaging?._id || (typeof (p as any).packaging === 'string' ? (p as any).packaging : undefined),
            size_id: (p as any).size?._id || (typeof (p as any).size === 'string' ? (p as any).size : undefined),
            collections: p.range ? [{ id: p.range._id, title: p.range.name, handle: p.range.slug }] : [],
            categories: Array.isArray(p.categories) ? p.categories.map(c => ({
              id: c._id,
              name: c.name || 'Untitled Category',
              handle: c.slug || 'untitled-category'
            })) : [],
            variants: [{
              id: `${p._id}_variant`,
              title: 'Default Variant',
              prices: [{ amount: p.price ? parseInt(p.price) * 100 : 0, currency_code: 'EUR' }],
              inventory_quantity: p.countInStock || 0
            }],
            thumbnail: getImageUrl(p.thumbnail || (p.images && (typeof p.images[0] === 'string' ? p.images[0] : p.images[0]?.url))),
            ad_image: p.ad_image ? getImageUrl(p.ad_image) : undefined,
            images: Array.isArray(p.images) ? p.images.map((img, index) => {
              // Handle both string filenames and object format {url, id}
              const filename = typeof img === 'string' ? img : img?.url
              const firstImage = typeof p.images[0] === 'string' ? p.images[0] : p.images[0]?.url
              return {
                id: `${p._id}_img_${index}`,
                url: getImageUrl(filename),
                isThumbnail: filename === (p.thumbnail || firstImage)
              }
            }) : [],
            status: p.published ? 'published' : 'draft',
            created_at: p.createdAt,
            updated_at: p.updatedAt
          })),
          count: sortedProducts.length,  // Total count after filters
          offset: offset,
          limit: limit
        }
      },
      retrieve: async (id: string, query?: any) => {
        const product = await apiClient.getProduct(id)
        return {
          product: {
            ...product,
            id: product._id,
            // Localized title depending on dashboard language
            title: (() => {
              const lang = (i18n?.language || 'fr').toLowerCase()
              if (lang.startsWith('fr')) return product.name || product.en?.name || product.slug || 'Untitled Product'
              return product.en?.name || product.name || product.slug || 'Untitled Product'
            })(),
            // Localize subtitle/description similarly for display
            subtitle: (() => {
              const lang = (i18n?.language || 'fr').toLowerCase()
              if (lang.startsWith('fr')) return product.subtitle || product.en?.subtitle || ''
              return product.en?.subtitle || product.subtitle || ''
            })(),
            description: (() => {
              const lang = (i18n?.language || 'fr').toLowerCase()
              if (lang.startsWith('fr')) return product.description || product.en?.description || ''
              return product.en?.description || product.description || ''
            })(),
            handle: product.slug || 'untitled-product',
            // Single collection object for sidebar/table
            collection: product.range ? { id: product.range._id, title: product.range.name, handle: product.range.slug } : undefined,
            collection_id: product.range?._id,
            type_id: (product as any).type?._id || (typeof (product as any).type === 'string' ? (product as any).type : undefined),
            packaging_id: (product as any).packaging?._id || (typeof (product as any).packaging === 'string' ? (product as any).packaging : undefined),
            size_id: (product as any).size?._id || (typeof (product as any).size === 'string' ? (product as any).size : undefined),
            // Optional typed refs for sidebar display
            type: (product as any).type && typeof (product as any).type === 'object' ? { id: (product as any).type._id, value: (product as any).type.name, handle: (product as any).type.slug } : undefined,
            packaging: (product as any).packaging && typeof (product as any).packaging === 'object' ? { id: (product as any).packaging._id, value: (product as any).packaging.name, handle: (product as any).packaging.slug } : undefined,
            size: (product as any).size && typeof (product as any).size === 'object' ? { id: (product as any).size._id, value: (product as any).size.name, handle: (product as any).size.slug } : undefined,
            collections: product.range ? [{ id: product.range._id, title: product.range.name, handle: product.range.slug }] : [],
            categories: Array.isArray(product.categories) ? product.categories.map(c => ({
              id: c._id,
              name: c.name || 'Untitled Category',
              handle: c.slug || 'untitled-category'
            })) : [],
            variants: [{
              id: `${product._id}_variant`,
              title: 'Default Variant',
              prices: [{ amount: product.price ? parseInt(product.price) * 100 : 0, currency_code: 'EUR' }],
              inventory_quantity: product.countInStock || 0
            }],
            thumbnail: getImageUrl(product.thumbnail || (product.images && (typeof product.images[0] === 'string' ? product.images[0] : product.images[0]?.url))),
            ad_image: product.ad_image ? getImageUrl(product.ad_image) : undefined,
            images: Array.isArray(product.images) ? product.images.map((img, index) => {
              // Handle both string filenames and object format {url, id}
              const filename = typeof img === 'string' ? img : img?.url
              const firstImage = typeof product.images[0] === 'string' ? product.images[0] : product.images[0]?.url
              return {
                id: `${product._id}_img_${index}`,
                url: getImageUrl(filename),
                isThumbnail: filename === (product.thumbnail || firstImage)
              }
            }) : [],
            status: product.published ? 'published' : 'draft',
            created_at: product.createdAt,
            updated_at: product.updatedAt
          }
        }
      },
      create: async (payload: any) => {
        // Extract filenames from images
        const images = payload.images ? payload.images.map((img: any) => {
          if (typeof img === 'string') return img
          if (img.url) {
            const urlParts = img.url.split('/')
            return urlParts[urlParts.length - 1]
          }
          return img
        }) : []

        // Extract filename from thumbnail
        let thumbnail = payload.thumbnail
        if (thumbnail && typeof thumbnail === 'string') {
          const urlParts = thumbnail.split('/')
          thumbnail = urlParts[urlParts.length - 1]
        }

        // Extract filename from ad_image
        let ad_image = payload.ad_image
        if (ad_image && typeof ad_image === 'string') {
          const urlParts = ad_image.split('/')
          ad_image = urlParts[urlParts.length - 1]
        }

        const productData: CreateProductRequest = {
          slug: payload.handle || payload.title?.toLowerCase().replace(/\s+/g, '-'),
          name: payload.title,
          description: payload.description,
          subtitle: payload.subtitle,
          reference: payload.reference,
          benefits: payload.benefits,
          how_to_use: payload.how_to_use,
          ingredients: payload.ingredients,
          // price/inventory not driven by variants anymore
          price: '0',
          countInStock: 0,
          en: {
            name: payload.en?.name || payload.title || "",
            description: payload.en?.description || payload.description || "",
            subtitle: payload.en?.subtitle || "",
            reference: payload.en?.reference || "",
            benefits: payload.en?.benefits || "",
            how_to_use: payload.en?.how_to_use || "",
            ingredients: payload.en?.ingredients || "",
          },
          published: payload.status === 'published',
          images: images,
          thumbnail: thumbnail,
          ad_image: ad_image,
          categories: Array.isArray(payload.categories)
            ? payload.categories.map((c: any) => (typeof c === 'string' ? c : c?.id)).filter(Boolean)
            : [],
          // @ts-ignore - backend accepts string[] as tags
          tags: payload.tags || [],
          range: payload.collection_id,
          // new references
          // @ts-ignore - CreateProductRequest may be broader; backend accepts below
          type: payload.type_id || undefined,
          // @ts-ignore
          packaging: payload.packaging_id || undefined,
          // @ts-ignore
          size: payload.size_id || undefined,
        }
        const product = await apiClient.createProduct(productData)
        return {
          product: {
            ...product,
            id: product._id,
            title: product.name,
            handle: product.slug
          }
        }
      },
      update: async (id: string, payload: any) => {
        const updateData: UpdateProductRequest = {}
        if (payload.title) {
          updateData.name = payload.title
          updateData.en = { ...updateData.en, name: payload.title }
        }
        if (payload.description) {
          updateData.description = payload.description
          updateData.en = { ...updateData.en, description: payload.description }
        }
        if (payload.en) {
          if (payload.en.name !== undefined) {
            updateData.en = { ...updateData.en, name: payload.en.name }
          }
          if (payload.en.description !== undefined) {
            updateData.en = { ...updateData.en, description: payload.en.description }
          }
          if (payload.en.subtitle !== undefined) {
            updateData.en = { ...updateData.en, subtitle: payload.en.subtitle }
          }
          if (payload.en.reference !== undefined) {
            updateData.en = { ...updateData.en, reference: payload.en.reference }
          }
          if (payload.en.benefits !== undefined) {
            updateData.en = { ...updateData.en, benefits: payload.en.benefits }
          }
          if (payload.en.how_to_use !== undefined) {
            updateData.en = { ...updateData.en, how_to_use: payload.en.how_to_use }
          }
          if (payload.en.ingredients !== undefined) {
            updateData.en = { ...updateData.en, ingredients: payload.en.ingredients }
          }
        }
        if (payload.subtitle !== undefined) updateData.subtitle = payload.subtitle
        if (payload.type_id !== undefined) (updateData as any).type = payload.type_id
        if (payload.packaging_id !== undefined) (updateData as any).packaging = payload.packaging_id
        if (payload.size_id !== undefined) (updateData as any).size = payload.size_id
        if (payload.reference !== undefined) updateData.reference = payload.reference
        if (payload.benefits !== undefined) updateData.benefits = payload.benefits
        if (payload.how_to_use !== undefined) updateData.how_to_use = payload.how_to_use
        if (payload.ingredients !== undefined) updateData.ingredients = payload.ingredients
        if (payload.handle) updateData.slug = payload.handle
        if (payload.status) updateData.published = payload.status === 'published'
        if (payload.variants?.[0]?.prices?.[0]?.amount) {
          updateData.price = (payload.variants[0].prices[0].amount / 100).toString()
        }
        if (payload.variants?.[0]?.inventory_quantity !== undefined) {
          updateData.countInStock = payload.variants[0].inventory_quantity
        }
        if (payload.images) {
          // Extract filenames from image URLs for backend storage
          updateData.images = payload.images.map((img: any) => {
            if (typeof img === 'string') return img
            if (img.url) {
              // Extract filename from URL
              const urlParts = img.url.split('/')
              return urlParts[urlParts.length - 1]
            }
            return img
          })
        }
        if (payload.thumbnail !== undefined) {
          // Extract filename from thumbnail URL
          if (typeof payload.thumbnail === 'string') {
            const urlParts = payload.thumbnail.split('/')
            updateData.thumbnail = urlParts[urlParts.length - 1]
          } else {
            updateData.thumbnail = payload.thumbnail
          }
        }
        if (payload.ad_image !== undefined) {
          // Extract filename from ad_image URL
          if (typeof payload.ad_image === 'string' && payload.ad_image) {
            const urlParts = payload.ad_image.split('/')
            updateData.ad_image = urlParts[urlParts.length - 1]
          } else {
            updateData.ad_image = payload.ad_image || ""
          }
        }
        if (payload.categories) {
          (updateData as any).categories = payload.categories.map((c: any) => (typeof c === 'string' ? c : c?.id)).filter(Boolean)
        }
        if (payload.collection_id) updateData.range = payload.collection_id
        if (payload.tags) (updateData as any).tags = payload.tags


        const product = await apiClient.updateProduct(id, updateData)
        return {
          product: {
            ...product,
            id: product._id,
            title: product.name,
            handle: product.slug
          }
        }
      },
      delete: async (id: string) => {
        await apiClient.deleteProduct(id)
        return {}
      },
      listVariants: async (productId: string, query?: any) => {
        const product = await apiClient.getProduct(productId)
        return {
          variants: [{
            id: `${product._id}_variant`,
            product_id: product._id,
            title: 'Default Variant',
            prices: [{ amount: parseInt(product.price) * 100, currency_code: 'EUR' }],
            inventory_quantity: product.countInStock
          }]
        }
      },
      retrieveVariant: async (productId: string, variantId: string, query?: any) => {
        const product = await apiClient.getProduct(productId)
        return {
          variant: {
            id: `${product._id}_variant`,
            product_id: product._id,
            title: 'Default Variant',
            prices: [{ amount: parseInt(product.price) * 100, currency_code: 'EUR' }],
            inventory_quantity: product.countInStock
          }
        }
      },
      createVariant: async (productId: string, payload: any) => {
        // For now, we'll update the main product since we don't have separate variants
        const updateData: UpdateProductRequest = {}
        if (payload.prices?.[0]?.amount) {
          updateData.price = (payload.prices[0].amount / 100).toString()
        }
        if (payload.inventory_quantity !== undefined) {
          updateData.countInStock = payload.inventory_quantity
        }
        const product = await apiClient.updateProduct(productId, updateData)
        return {
          variant: {
            id: `${product._id}_variant`,
            product_id: product._id,
            title: 'Default Variant',
            prices: [{ amount: parseInt(product.price) * 100, currency_code: 'EUR' }],
            inventory_quantity: product.countInStock
          }
        }
      },
      updateVariant: async (productId: string, variantId: string, payload: any) => {
        const updateData: UpdateProductRequest = {}
        if (payload.prices?.[0]?.amount) {
          updateData.price = (payload.prices[0].amount / 100).toString()
        }
        if (payload.inventory_quantity !== undefined) {
          updateData.countInStock = payload.inventory_quantity
        }
        const product = await apiClient.updateProduct(productId, updateData)
        return {
          variant: {
            id: `${product._id}_variant`,
            product_id: product._id,
            title: 'Default Variant',
            prices: [{ amount: parseInt(product.price) * 100, currency_code: 'EUR' }],
            inventory_quantity: product.countInStock
          }
        }
      },
      deleteVariant: async (productId: string, variantId: string) => {
        // Can't delete the only variant, so we'll just return success
        return {}
      }
    },
    productType: {
      list: async (query?: any) => {
        const items = await apiClient.getProductTypes(query?.all)
        return {
          product_types: items.map((it) => ({ id: it._id, value: it.name, handle: it.slug })),
          count: items.length,
          offset: 0,
          limit: items.length,
        }
      },
      retrieve: async (id: string, _query?: any) => {
        const it = await apiClient.getProductType(id)
        return { product_type: { id: it._id, value: it.name, handle: it.slug } }
      },
      create: async (payload: any) => {
        const data = await apiClient.createProductType({
          slug: payload.handle || (payload.value || '').toLowerCase().replace(/\s+/g, '-'),
          name: payload.value,
          en: payload.en || { name: payload.value },
          published: payload.status ? payload.status === 'published' : true,
        })
        return { product_type: { id: data._id, value: data.name, handle: data.slug } }
      },
      update: async (id: string, payload: any) => {
        const upd: any = {}
        if (payload.value) upd.name = payload.value
        if (payload.handle) upd.slug = payload.handle
        if (payload.en) upd.en = payload.en
        const data = await apiClient.updateProductType(id, upd)
        return { product_type: { id: data._id, value: data.name, handle: data.slug } }
      },
      delete: async (id: string) => {
        await apiClient.deleteProductType(id)
        return { deleted: true, id }
      },
    },
    packaging: {
      list: async (query?: any) => {
        const items = await apiClient.getPackaging(query?.all)
        return {
          packaging: items.map((it) => ({ id: it._id, value: it.name, handle: it.slug })),
          count: items.length,
          offset: 0,
          limit: items.length,
        }
      },
      retrieve: async (id: string, _query?: any) => {
        const it = await apiClient.getOnePackaging(id)
        return { packaging: { id: it._id, value: it.name, handle: it.slug } }
      },
      create: async (payload: any) => {
        const data = await apiClient.createPackaging({
          slug: payload.handle || (payload.value || '').toLowerCase().replace(/\s+/g, '-'),
          name: payload.value,
          en: payload.en || { name: payload.value },
          published: payload.status ? payload.status === 'published' : true,
        })
        return { packaging: { id: data._id, value: data.name, handle: data.slug } }
      },
      update: async (id: string, payload: any) => {
        const upd: any = {}
        if (payload.value) upd.name = payload.value
        if (payload.handle) upd.slug = payload.handle
        if (payload.en) upd.en = payload.en
        const data = await apiClient.updatePackaging(id, upd)
        return { packaging: { id: data._id, value: data.name, handle: data.slug } }
      },
      delete: async (id: string) => {
        await apiClient.deletePackaging(id)
        return { deleted: true, id }
      },
    },
    size: {
      list: async (query?: any) => {
        const items = await apiClient.getSizes(query?.all)
        return {
          sizes: items.map((it) => ({ id: it._id, value: it.name, handle: it.slug })),
          count: items.length,
          offset: 0,
          limit: items.length,
        }
      },
      retrieve: async (id: string, _query?: any) => {
        const it = await apiClient.getSize(id)
        return { size: { id: it._id, value: it.name, handle: it.slug } }
      },
      create: async (payload: any) => {
        const data = await apiClient.createSize({
          slug: payload.handle || (payload.value || '').toLowerCase().replace(/\s+/g, '-'),
          name: payload.value,
          en: payload.en || { name: payload.value },
          published: payload.status ? payload.status === 'published' : true,
        })
        return { size: { id: data._id, value: data.name, handle: data.slug } }
      },
      update: async (id: string, payload: any) => {
        const upd: any = {}
        if (payload.value) upd.name = payload.value
        if (payload.handle) upd.slug = payload.handle
        if (payload.en) upd.en = payload.en
        const data = await apiClient.updateSize(id, upd)
        return { size: { id: data._id, value: data.name, handle: data.slug } }
      },
      delete: async (id: string) => {
        await apiClient.deleteSize(id)
        return { deleted: true, id }
      },
    },
    productTag: {
      list: async (query?: any) => {
        const items = await apiClient.getTags(query?.all)
        return {
          product_tags: items.map((it) => ({ id: it._id, value: it.name, handle: it.slug })),
          count: items.length,
          offset: 0,
          limit: items.length,
        }
      },
      retrieve: async (id: string, _query?: any) => {
        const it = await apiClient.getTag(id)
        return { product_tag: { id: it._id, value: it.name, handle: it.slug } }
      },
      create: async (payload: any) => {
        const data = await apiClient.createTag({
          slug: payload.handle || (payload.value || '').toLowerCase().replace(/\s+/g, '-'),
          name: payload.value,
          en: payload.en || { name: payload.value },
          published: payload.status ? payload.status === 'published' : true,
        })
        return { product_tag: { id: data._id, value: data.name, handle: data.slug } }
      },
      update: async (id: string, payload: any) => {
        const upd: any = {}
        if (payload.value) upd.name = payload.value
        if (payload.handle) upd.slug = payload.handle
        if (payload.en) upd.en = payload.en
        const data = await apiClient.updateTag(id, upd)
        return { product_tag: { id: data._id, value: data.name, handle: data.slug } }
      },
      delete: async (id: string) => {
        await apiClient.deleteTag(id)
        return { deleted: true, id }
      },
    },
    productCollection: {
      list: async (query?: any) => {
        const ranges = await apiClient.getRanges(query)
        // Trier par date de création décroissante (plus récent en premier)
        const sortedRanges = ranges.sort((a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )
        return {
          collections: sortedRanges.map(r => ({
            id: r._id,
            title: r.name || 'Untitled Collection',
            handle: r.slug || 'untitled-collection',
            products: Array.isArray(r.products) ? r.products.map(p => ({
              id: p._id,
              title: p.name || 'Untitled Product'
            })) : [],
            image: r.image,
            created_at: r.createdAt,
            updated_at: r.updatedAt
          })),
          count: ranges.length,
          offset: 0,
          limit: ranges.length
        }
      },
      retrieve: async (id: string, query?: any) => {
        const range = await apiClient.getRange(id)
        return {
          collection: {
            id: range._id,
            title: range.name || 'Untitled Collection',
            handle: range.slug || 'untitled-collection',
            products: Array.isArray(range.products) ? range.products.map(p => ({
              id: p._id,
              title: p.name || 'Untitled Product'
            })) : [],
            image: range.image,
            created_at: range.createdAt,
            updated_at: range.updatedAt
          }
        }
      },
      create: async (payload: any) => {
        const rangeData: CreateRangeRequest = {
          slug: payload.handle || payload.title?.toLowerCase().replace(/\s+/g, '-'),
          name: payload.title,
          en: payload.en || {
            name: payload.title
          },
          published: payload.status === 'published' || true,
          image: payload.image
        }
        const range = await apiClient.createRange(rangeData)
        return {
          collection: {
            id: range._id,
            title: range.name,
            handle: range.slug,
            products: [],
            image: range.image,
            created_at: range.createdAt,
            updated_at: range.updatedAt
          }
        }
      },
      update: async (id: string, payload: any) => {
        const updateData: UpdateRangeRequest = {}
        if (payload.title) {
          updateData.name = payload.title
          updateData.en = { name: payload.title }
        }
        if (payload.handle) updateData.slug = payload.handle
        if (payload.image) updateData.image = payload.image

        const range = await apiClient.updateRange(id, updateData)
        return {
          collection: {
            id: range._id,
            title: range.name,
            handle: range.slug,
            products: Array.isArray(range.products) ? range.products.map(p => ({
              id: p._id,
              title: p.name || 'Untitled Product'
            })) : [],
            image: range.image,
            created_at: range.createdAt,
            updated_at: range.updatedAt
          }
        }
      },
      updateProducts: async (id: string, payload: any) => {
        // Handle adding/removing products from collection
        if (payload.add) {
          for (const productId of payload.add) {
            await apiClient.addProductToRange(id, productId)
          }
        }
        if (payload.remove) {
          for (const productId of payload.remove) {
            await apiClient.removeProductFromRange(id, productId)
          }
        }
        const range = await apiClient.getRange(id)
        return {
          collection: {
            id: range._id,
            title: range.name,
            handle: range.slug,
            products: Array.isArray(range.products) ? range.products.map(p => ({
              id: p._id,
              title: p.name || 'Untitled Product'
            })) : [],
            image: range.image,
            created_at: range.createdAt,
            updated_at: range.updatedAt
          }
        }
      },
      delete: async (id: string) => {
        await apiClient.deleteRange(id)
        return {}
      }
    },
    productCategory: {
      list: async () => {
        const categories = await apiClient.getCategories()
        // Trier par date de création décroissante (plus récent en premier)
        const sortedCategories = categories.sort((a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )
        return {
          product_categories: sortedCategories.map(c => ({
            id: c._id,
            name: c.name || 'Untitled Category',
            handle: c.slug || 'untitled-category',
            description: c.description || '',
            is_active: c.published !== false,
            is_internal: false,
            created_at: c.createdAt,
            updated_at: c.updatedAt
          })),
          count: categories.length,
          offset: 0,
          limit: categories.length
        }
      },
      retrieve: async (id: string) => {
        const category = await apiClient.getCategory(id)
        return {
          product_category: {
            id: category._id,
            name: category.name || 'Untitled Category',
            handle: category.slug || 'untitled-category',
            description: category.description || '',
            is_active: category.published !== false,
            is_internal: false,
            created_at: category.createdAt,
            updated_at: category.updatedAt
          }
        }
      },
      create: async (payload: any) => {
        const categoryData = {
          slug: payload.handle || payload.name?.toLowerCase().replace(/\s+/g, '-'),
          name: payload.name,
          description: payload.description || '',
          en: payload.en || {
            name: payload.name,
            description: payload.description || ''
          },
          published: true
        }
        const category = await apiClient.createCategory(categoryData)
        return {
          product_category: {
            id: category._id,
            name: category.name,
            handle: category.slug,
            description: category.description || '',
            is_active: category.published !== false,
            is_internal: false,
            created_at: category.createdAt,
            updated_at: category.updatedAt
          }
        }
      },
      update: async (id: string, payload: any) => {
        const updateData: any = {}
        if (payload.name) {
          updateData.name = payload.name
        }
        if (payload.en) {
          updateData.en = payload.en
        } else if (payload.name) {
          updateData.en = { name: payload.name }
        }
        if (payload.handle) updateData.slug = payload.handle
        if (payload.description !== undefined) updateData.description = payload.description

        const category = await apiClient.updateCategory(id, updateData)
        return {
          product_category: {
            id: category._id,
            name: category.name,
            handle: category.slug,
            description: category.description || '',
            is_active: category.published !== false,
            is_internal: false,
            created_at: category.createdAt,
            updated_at: category.updatedAt
          }
        }
      },
      updateProducts: async (id: string, payload: any) => {
        // Handle adding/removing products from category
        // Note: This functionality may need to be implemented in the backend
        if (payload.add) {
          // TODO: Implement add products to category in backend
          console.warn('Add products to category not yet implemented')
        }
        if (payload.remove) {
          // TODO: Implement remove products from category in backend
          console.warn('Remove products from category not yet implemented')
        }
        const category = await apiClient.getCategory(id)
        return {
          product_category: {
            id: category._id,
            name: category.name,
            handle: category.slug,
            description: category.description || '',
            is_active: category.published !== false,
            is_internal: false,
            created_at: category.createdAt,
            updated_at: category.updatedAt
          }
        }
      },
      delete: async (id: string) => {
        await apiClient.deleteCategory(id)
        return { deleted: true, id }
      }
    },
    order: {
      list: async (query?: any) => {
        // Handle customer_id filtering
        let orders = await apiClient.getOrders(query)

        // Filter by customer_id if provided
        if (query?.customer_id) {
          orders = orders.filter(o => o.userId?._id === query.customer_id)
        }

        return {
          orders: orders.map(o => ({
            ...o,
            id: o._id,
            display_id: o.orderNumber.replace('#', ''),
            customer: o.userId ? {
              id: o.userId._id,
              first_name: o.userId.firstname,
              last_name: o.userId.lastname,
              email: o.userId.email
            } : {
              id: 'unknown',
              first_name: 'Unknown',
              last_name: 'Customer',
              email: 'unknown@example.com'
            },
            items: o.products?.map(p => ({
              id: `${o._id}_${p.productId?._id || 'unknown'}`,
              title: p.productId?.name || 'Unknown Product',
              quantity: p.quantity || 0,
              unit_price: p.productId?.price ? parseInt(p.productId.price) * 100 : 0,
              total: p.productId?.price ? parseInt(p.productId.price) * p.quantity * 100 : 0
            })) || [],
            total: (o.amount || 0) * 100,
            currency_code: 'EUR',
            fulfillment_status: o.status === 'delivered' ? 'fulfilled' : 'not_fulfilled',
            payment_status: 'captured',
            created_at: o.createdAt,
            updated_at: o.updatedAt
          })),
          count: orders.length,
          offset: 0,
          limit: orders.length
        }
      },
      retrieve: async (id: string, query?: any) => {
        const order = await apiClient.getOrder(id)
        if (!order || !order._id) {
          throw new FetchError('Order not found', 404, 'Not Found')
        }
        const displayId = typeof order.orderNumber === 'string' ? order.orderNumber.replace('#', '') : ''
        const items = (order.products || []).map((p, index) => {
          const unit = p.productId?.price ? parseInt(p.productId.price) * 100 : 0
          const qty = p.quantity || 0
          const subtotal = unit * qty
          const img0 = Array.isArray(p.productId?.images) ? (p.productId as any).images[0] : undefined
          const imgName = typeof img0 === 'string' ? img0 : (img0 as any)?.url
          const thumb = getImageUrl(p.productId?.thumbnail || imgName)
          return {
            id: `${order._id}_${p.productId?._id || 'unknown'}_${index}`,
            title: p.productId?.name || 'Unknown Product',
            quantity: qty,
            unit_price: unit,
            subtotal,
            total: subtotal,
            thumbnail: thumb,
            // fields expected by UI
            requires_shipping: !(p.productId?.is_giftcard === true),
            detail: { fulfilled_quantity: 0 },
            tax_lines: [],
            adjustments: [],
            variant: { options: [] },
          }
        })
        const item_subtotal = items.reduce((sum, it) => sum + (it.subtotal || 0), 0)
        const total = (order.amount || item_subtotal || 0) * 100
        return {
          order: {
            ...order,
            id: order._id,
            display_id: displayId,
            customer: order.userId ? {
              id: order.userId._id,
              first_name: order.userId.firstname,
              last_name: order.userId.lastname,
              email: order.userId.email
            } : {
              id: 'unknown',
              first_name: 'Unknown',
              last_name: 'Customer',
              email: 'unknown@example.com'
            },
            items,
            item_subtotal,
            shipping_methods: [],
            shipping_subtotal: 0,
            original_tax_total: 0,
            discount_total: 0,
            original_total: total,
            total,
            credit_lines: [],
            payments: [],
            payment_collections: [],
            currency_code: 'EUR',
            fulfillment_status: order.status === 'delivered' ? 'fulfilled' : 'not_fulfilled',
            payment_status: 'captured',
            created_at: order.createdAt,
            updated_at: order.updatedAt
          }
        }
      },
      update: async (id: string, payload: any) => {
        const updateData: UpdateOrderRequest = {}
        if (payload.status) updateData.status = payload.status

        const order = await apiClient.updateOrder(id, updateData)
        const items2 = (order.products || []).map((p, index) => {
          const unit = p.productId?.price ? parseInt(p.productId.price) * 100 : 0
          const qty = p.quantity || 0
          const subtotal = unit * qty
          const img0 = Array.isArray(p.productId?.images) ? (p.productId as any).images[0] : undefined
          const imgName = typeof img0 === 'string' ? img0 : (img0 as any)?.url
          const thumb = getImageUrl(p.productId?.thumbnail || imgName)
          return {
            id: `${order._id}_${p.productId?._id || 'unknown'}_${index}`,
            title: p.productId?.name || 'Unknown Product',
            quantity: qty,
            unit_price: unit,
            subtotal,
            total: subtotal,
            thumbnail: thumb,
            // fields expected by UI
            requires_shipping: !(p.productId?.is_giftcard === true),
            detail: { fulfilled_quantity: 0 },
            tax_lines: [],
            adjustments: [],
            variant: { options: [] },
          }
        })
        const item_subtotal2 = items2.reduce((sum, it) => sum + (it.subtotal || 0), 0)
        const total2 = (order.amount || item_subtotal2 || 0) * 100
        return {
          order: {
            ...order,
            id: order._id,
            display_id: order.orderNumber.replace('#', ''),
            customer: order.userId ? {
              id: order.userId._id,
              first_name: order.userId.firstname,
              last_name: order.userId.lastname,
              email: order.userId.email
            } : {
              id: 'unknown',
              first_name: 'Unknown',
              last_name: 'Customer',
              email: 'unknown@example.com'
            },
            items: items2,
            item_subtotal: item_subtotal2,
            shipping_methods: [],
            shipping_subtotal: 0,
            original_tax_total: 0,
            discount_total: 0,
            original_total: total2,
            total: total2,
            credit_lines: [],
            payments: [],
            currency_code: 'EUR',
            fulfillment_status: order.status === 'delivered' ? 'fulfilled' : order.status,
            payment_status: 'captured',
            status: order.status,
            created_at: order.createdAt,
            updated_at: order.updatedAt
          }
        }
      },
      retrievePreview: async (id: string, query?: any) => {
        // Order preview is used for edit workflows; return the order with order_change: null
        const order = await apiClient.getOrder(id)
        if (!order || !order._id) {
          throw new FetchError('Order not found', 404, 'Not Found')
        }
        const displayId = typeof order.orderNumber === 'string' ? order.orderNumber.replace('#', '') : ''
        const items = (order.products || []).map((p, index) => {
          const unit = p.productId?.price ? parseInt(p.productId.price) * 100 : 0
          const qty = p.quantity || 0
          const subtotal = unit * qty
          const img0 = Array.isArray(p.productId?.images) ? (p.productId as any).images[0] : undefined
          const imgName = typeof img0 === 'string' ? img0 : (img0 as any)?.url
          const thumb = getImageUrl(p.productId?.thumbnail || imgName)
          return {
            id: `${order._id}_${p.productId?._id || 'unknown'}_${index}`,
            title: p.productId?.name || 'Unknown Product',
            quantity: qty,
            unit_price: unit,
            subtotal,
            total: subtotal,
            thumbnail: thumb,
            requires_shipping: !(p.productId?.is_giftcard === true),
            detail: { fulfilled_quantity: 0 },
            tax_lines: [],
            adjustments: [],
            variant: { options: [] },
          }
        })
        const item_subtotal = items.reduce((sum, it) => sum + (it.subtotal || 0), 0)
        const total = (order.amount || item_subtotal || 0) * 100
        return {
          order: {
            ...order,
            id: order._id,
            display_id: displayId,
            customer: order.userId ? {
              id: order.userId._id,
              first_name: order.userId.firstname,
              last_name: order.userId.lastname,
              email: order.userId.email
            } : {
              id: 'unknown',
              first_name: 'Unknown',
              last_name: 'Customer',
              email: 'unknown@example.com'
            },
            items,
            item_subtotal,
            shipping_methods: [],
            shipping_subtotal: 0,
            original_tax_total: 0,
            discount_total: 0,
            original_total: total,
            total,
            credit_lines: [],
            payments: [],
            payment_collections: [],
            currency_code: 'EUR',
            fulfillment_status: order.status === 'delivered' ? 'fulfilled' : 'not_fulfilled',
            payment_status: 'captured',
            created_at: order.createdAt,
            updated_at: order.updatedAt,
            order_change: null,
          }
        }
      }
    },
    customer: {
      list: async (query?: any) => {
        const users = await apiClient.getUsers({ ...query, admin: false })
        return {
          customers: users.map(u => ({
            ...u,
            id: u._id,
            first_name: u.firstname,
            last_name: u.lastname,
            has_account: true,
            phone: u.addresses?.[0]?.zip || '', // Using zip field as phone placeholder
            company_name: '', // Not available in current user model
            addresses: u.addresses?.map(addr => ({
              id: `${u._id}_${addr.street}_${addr.city}`,
              first_name: u.firstname,
              last_name: u.lastname,
              company: '',
              address_1: addr.street,
              address_2: '',
              city: addr.city,
              province: addr.state || '',
              postal_code: addr.zip || '',
              country_code: addr.country?.toLowerCase() || 'fr',
              phone: '',
              metadata: null,
              created_at: u.createdAt,
              updated_at: u.updatedAt
            })) || [],
            orders: [], // Will be populated when needed
            groups: [], // Customer groups not implemented yet
            metadata: null,
            created_at: u.createdAt,
            updated_at: u.updatedAt
          })),
          count: users.length,
          offset: 0,
          limit: users.length
        }
      },
      retrieve: async (id: string, query?: any) => {
        const user = await apiClient.getUser(id)
        return {
          customer: {
            ...user,
            id: user._id,
            first_name: user.firstname,
            last_name: user.lastname,
            has_account: true,
            phone: user.addresses?.[0]?.zip || '', // Using zip field as phone placeholder
            company_name: '', // Not available in current user model
            addresses: user.addresses?.map(addr => ({
              id: `${user._id}_${addr.street}_${addr.city}`,
              first_name: user.firstname,
              last_name: user.lastname,
              company: '',
              address_1: addr.street,
              address_2: '',
              city: addr.city,
              province: addr.state || '',
              postal_code: addr.zip || '',
              country_code: addr.country?.toLowerCase() || 'fr',
              phone: '',
              metadata: null,
              created_at: user.createdAt,
              updated_at: user.updatedAt
            })) || [],
            orders: [], // Will be populated when needed
            groups: [], // Customer groups not implemented yet
            metadata: null,
            created_at: user.createdAt,
            updated_at: user.updatedAt
          }
        }
      },
      create: async (payload: any) => {
        const userData = {
          firstname: payload.first_name || '',
          lastname: payload.last_name || '',
          username: payload.email, // Use email as username
          email: payload.email,
          password: 'temp-password-' + Math.random().toString(36).substring(7), // Generate temp password
          role: 'user' as const
        }
        const user = await apiClient.register(userData)
        return {
          customer: {
            ...user,
            id: user._id,
            first_name: user.firstname,
            last_name: user.lastname,
            has_account: true,
            phone: payload.phone || '',
            company_name: payload.company_name || '',
            addresses: [],
            orders: [],
            groups: [],
            metadata: null,
            created_at: user.createdAt,
            updated_at: user.updatedAt
          }
        }
      },
      update: async (id: string, payload: any) => {
        const updateData: Partial<User> = {}
        if (payload.first_name) updateData.firstname = payload.first_name
        if (payload.last_name) updateData.lastname = payload.last_name
        if (payload.email) updateData.email = payload.email

        const user = await apiClient.updateUser(id, updateData)
        return {
          customer: {
            ...user,
            id: user._id,
            first_name: user.firstname,
            last_name: user.lastname,
            has_account: true,
            phone: payload.phone || user.addresses?.[0]?.zip || '',
            company_name: payload.company_name || '',
            addresses: user.addresses?.map(addr => ({
              id: `${user._id}_${addr.street}_${addr.city}`,
              first_name: user.firstname,
              last_name: user.lastname,
              company: '',
              address_1: addr.street,
              address_2: '',
              city: addr.city,
              province: addr.state || '',
              postal_code: addr.zip || '',
              country_code: addr.country?.toLowerCase() || 'fr',
              phone: '',
              metadata: null,
              created_at: user.createdAt,
              updated_at: user.updatedAt
            })) || [],
            orders: [],
            groups: [],
            metadata: null,
            created_at: user.createdAt,
            updated_at: user.updatedAt
          }
        }
      },
      delete: async (id: string) => {
        await apiClient.deleteUser(id)
        return { id, object: 'customer', deleted: true }
      },
      // Address management methods
      createAddress: async (id: string, payload: any) => {
        // For now, we'll update the user's addresses array
        const user = await apiClient.getUser(id)
        const newAddress = {
          street: payload.address_1,
          city: payload.city,
          state: payload.province,
          zip: payload.postal_code,
          country: payload.country_code?.toUpperCase() || 'FR',
          principal: user.addresses?.length === 0 // First address is principal
        }

        const updatedAddresses = [...(user.addresses || []), newAddress]
        const updatedUser = await apiClient.updateUser(id, { addresses: updatedAddresses })

        return {
          customer: {
            ...updatedUser,
            id: updatedUser._id,
            first_name: updatedUser.firstname,
            last_name: updatedUser.lastname,
            has_account: true,
            addresses: updatedUser.addresses?.map(addr => ({
              id: `${updatedUser._id}_${addr.street}_${addr.city}`,
              first_name: updatedUser.firstname,
              last_name: updatedUser.lastname,
              company: '',
              address_1: addr.street,
              address_2: '',
              city: addr.city,
              province: addr.state || '',
              postal_code: addr.zip || '',
              country_code: addr.country?.toLowerCase() || 'fr',
              phone: '',
              metadata: null,
              created_at: updatedUser.createdAt,
              updated_at: updatedUser.updatedAt
            })) || []
          }
        }
      },
      updateAddress: async (id: string, addressId: string, payload: any) => {
        const user = await apiClient.getUser(id)
        const addressIndex = user.addresses?.findIndex(addr =>
          `${user._id}_${addr.street}_${addr.city}` === addressId
        ) ?? -1

        if (addressIndex === -1) {
          throw new FetchError('Address not found', 404)
        }

        const updatedAddresses = [...(user.addresses || [])]
        updatedAddresses[addressIndex] = {
          ...updatedAddresses[addressIndex],
          street: payload.address_1 || updatedAddresses[addressIndex].street,
          city: payload.city || updatedAddresses[addressIndex].city,
          state: payload.province || updatedAddresses[addressIndex].state,
          zip: payload.postal_code || updatedAddresses[addressIndex].zip,
          country: payload.country_code?.toUpperCase() || updatedAddresses[addressIndex].country
        }

        const updatedUser = await apiClient.updateUser(id, { addresses: updatedAddresses })

        return {
          customer: {
            ...updatedUser,
            id: updatedUser._id,
            first_name: updatedUser.firstname,
            last_name: updatedUser.lastname,
            has_account: true,
            addresses: updatedUser.addresses?.map(addr => ({
              id: `${updatedUser._id}_${addr.street}_${addr.city}`,
              first_name: updatedUser.firstname,
              last_name: updatedUser.lastname,
              company: '',
              address_1: addr.street,
              address_2: '',
              city: addr.city,
              province: addr.state || '',
              postal_code: addr.zip || '',
              country_code: addr.country?.toLowerCase() || 'fr',
              phone: '',
              metadata: null,
              created_at: updatedUser.createdAt,
              updated_at: updatedUser.updatedAt
            })) || []
          }
        }
      },
      deleteAddress: async (id: string, addressId: string) => {
        const user = await apiClient.getUser(id)
        const updatedAddresses = user.addresses?.filter(addr =>
          `${user._id}_${addr.street}_${addr.city}` !== addressId
        ) || []

        const updatedUser = await apiClient.updateUser(id, { addresses: updatedAddresses })

        return {
          customer: {
            ...updatedUser,
            id: updatedUser._id,
            first_name: updatedUser.firstname,
            last_name: updatedUser.lastname,
            has_account: true,
            addresses: updatedUser.addresses?.map(addr => ({
              id: `${updatedUser._id}_${addr.street}_${addr.city}`,
              first_name: updatedUser.firstname,
              last_name: updatedUser.lastname,
              company: '',
              address_1: addr.street,
              address_2: '',
              city: addr.city,
              province: addr.state || '',
              postal_code: addr.zip || '',
              country_code: addr.country?.toLowerCase() || 'fr',
              phone: '',
              metadata: null,
              created_at: updatedUser.createdAt,
              updated_at: updatedUser.updatedAt
            })) || []
          }
        }
      },
      listAddresses: async (id: string, query?: any) => {
        const user = await apiClient.getUser(id)
        return {
          customer: {
            ...user,
            id: user._id,
            first_name: user.firstname,
            last_name: user.lastname,
            addresses: user.addresses?.map(addr => ({
              id: `${user._id}_${addr.street}_${addr.city}`,
              first_name: user.firstname,
              last_name: user.lastname,
              company: '',
              address_1: addr.street,
              address_2: '',
              city: addr.city,
              province: addr.state || '',
              postal_code: addr.zip || '',
              country_code: addr.country?.toLowerCase() || 'fr',
              phone: '',
              metadata: null,
              created_at: user.createdAt,
              updated_at: user.updatedAt
            })) || []
          }
        }
      },
      retrieveAddress: async (id: string, addressId: string) => {
        const user = await apiClient.getUser(id)
        const address = user.addresses?.find(addr =>
          `${user._id}_${addr.street}_${addr.city}` === addressId
        )

        if (!address) {
          throw new FetchError('Address not found', 404)
        }

        return {
          customer: {
            ...user,
            id: user._id,
            first_name: user.firstname,
            last_name: user.lastname,
            addresses: [{
              id: addressId,
              first_name: user.firstname,
              last_name: user.lastname,
              company: '',
              address_1: address.street,
              address_2: '',
              city: address.city,
              province: address.state || '',
              postal_code: address.zip || '',
              country_code: address.country?.toLowerCase() || 'fr',
              phone: '',
              metadata: null,
              created_at: user.createdAt,
              updated_at: user.updatedAt
            }]
          }
        }
      },
      // Customer group methods (placeholder for future implementation)
      batchCustomerGroups: async (id: string, payload: any) => {
        // Customer groups not implemented in current backend
        const user = await apiClient.getUser(id)
        return {
          customer: {
            ...user,
            id: user._id,
            first_name: user.firstname,
            last_name: user.lastname,
            has_account: true,
            groups: [], // Empty for now
            created_at: user.createdAt,
            updated_at: user.updatedAt
          }
        }
      }
    },
    user: {
      me: async (query?: any) => {
        const user = apiClient.getCurrentUser()
        if (!user) {
          throw new FetchError('No authenticated user', 401, 'Unauthorized')
        }
        return {
          user: {
            ...user,
            id: user._id,
            first_name: user.firstname,
            last_name: user.lastname,
            created_at: user.createdAt,
            updated_at: user.updatedAt
          }
        }
      },
      list: async (query?: any) => {
        const users = await apiClient.getUsers({ ...query, admin: true })
        return {
          users: users.map(u => ({
            ...u,
            id: u._id,
            first_name: u.firstname,
            last_name: u.lastname,
            created_at: u.createdAt,
            updated_at: u.updatedAt
          })),
          count: users.length,
          offset: 0,
          limit: users.length
        }
      },
      retrieve: async (id: string, query?: any) => {
        const user = await apiClient.getUser(id)
        return {
          user: {
            ...user,
            id: user._id,
            first_name: user.firstname,
            last_name: user.lastname,
            created_at: user.createdAt,
            updated_at: user.updatedAt
          }
        }
      },
      create: async (payload: any, query?: any) => {
        const userData = {
          firstname: payload.first_name,
          lastname: payload.last_name,
          username: payload.email, // Use email as username
          email: payload.email,
          password: payload.password || 'temp-password',
          role: payload.role || 'user'
        }
        const user = await apiClient.register(userData)
        return {
          user: {
            ...user,
            id: user._id,
            first_name: user.firstname,
            last_name: user.lastname,
            created_at: user.createdAt,
            updated_at: user.updatedAt
          }
        }
      },
      update: async (id: string, payload: any, query?: any) => {
        const updateData: Partial<User> = {}
        if (payload.first_name) updateData.firstname = payload.first_name
        if (payload.last_name) updateData.lastname = payload.last_name
        if (payload.email) updateData.email = payload.email
        if (payload.role) updateData.role = payload.role

        const user = await apiClient.updateUser(id, updateData)
        return {
          user: {
            ...user,
            id: user._id,
            first_name: user.firstname,
            last_name: user.lastname,
            created_at: user.createdAt,
            updated_at: user.updatedAt
          }
        }
      },
      delete: async (id: string) => {
        await apiClient.deleteUser(id)
        return {}
      }
    },
    store: {
      retrieve: async (query?: any) => {
        const settings = await apiClient.getStoreSettings()
        // Map our custom settings to Medusa store format for compatibility
        return {
          store: {
            id: settings.id || settings._id || "store_01",
            name: settings.store_name || "Laboratoire Luxe Cosmétique",
            default_currency_code: "EUR",
            default_location_id: "loc_01",
            created_at: settings.created_at || settings.createdAt,
            updated_at: settings.updated_at || settings.updatedAt,
            // Include our custom settings
            ...settings
          }
        }
      },
      list: async (query?: any) => {
        const settings = await apiClient.getStoreSettings()
        return {
          stores: [{
            id: settings.id || settings._id || "store_01",
            name: settings.store_name || "Laboratoire Luxe Cosmétique",
            default_currency_code: "EUR",
            default_location_id: "loc_01",
            created_at: settings.created_at || settings.createdAt,
            updated_at: settings.updated_at || settings.updatedAt,
            ...settings
          }]
        }
      },
      update: async (id: string, payload: any) => {
        // Map Medusa format to our custom settings format
        const updateData: UpdateStoreSettingsRequest = {}
        if (payload.name) updateData.store_name = payload.name
        if (payload.store_name) updateData.store_name = payload.store_name
        if (payload.phone_number) updateData.phone_number = payload.phone_number
        if (payload.email) updateData.email = payload.email
        if (payload.phones !== undefined) updateData.phones = payload.phones
        if (payload.others !== undefined) updateData.others = payload.others
        if (payload.address) updateData.address = payload.address
        if (payload.social_media) updateData.social_media = payload.social_media
        if (payload.seo) updateData.seo = payload.seo
        if (payload.business_info) updateData.business_info = payload.business_info

        const settings = await apiClient.updateStoreSettings(updateData)
        return {
          store: {
            id: settings.id,
            name: settings.store_name,
            default_currency_code: "EUR",
            default_location_id: "loc_01",
            created_at: settings.created_at,
            updated_at: settings.updated_at,
            ...settings
          }
        }
      },
    },
    upload: {
      create: async (payload: { files: File[] }) => {
        // 1) Upload to temp on backend
        const uploadResults = await apiClient.uploadFiles(payload.files)

        // 2) Persist each uploaded file (FTP in production)
        const filenames = uploadResults.map((result, index) =>
          // Extract filename from server temp path as backend expects just the filename
          (result?.path || '').replace(/^.*[\\\/]/, '') || payload.files[index]?.name || 'unknown'
        )
        await Promise.all(
          filenames.map((name, idx) => {
            const type = payload.files[idx]?.type || ''
            const folder = type.startsWith('image/') ? 'products' : 'ads'
            return apiClient.saveUploadedFile(name, folder)
          })
        )

        // 3) Return normalized response
        return {
          files: filenames.map((name, index) => {
            const type = payload.files[index]?.type || ''
            const isImage = type.startsWith('image/')
            return {
              id: Math.random().toString(36).substring(7),
              url: (isImage ? (getImageUrl(name) ?? "") : getAssetUrl(name)) as string,
              filename: payload.files[index]?.name || name,
              size: payload.files[index]?.size ?? 0,
              mimetype: type || 'application/octet-stream',
              created_at: new Date().toISOString(),
            }
          }),
        }
      },
      delete: async (fileId: string) => {
        // For existing backend, we need the filename, not the ID
        // This is a limitation that would need to be addressed in a real implementation
        await apiClient.deleteUploadedFile(fileId)
        return {}
      }
    },
    site: {
      get: async () => {
        const config = await apiClient.getSiteConfig()
        return { config }
      },
      update: async (payload: Partial<SiteConfig>) => {
        const config = await apiClient.updateSiteConfig(payload)
        return { config }
      }
    }
  }
}

// Export the API client for direct use
export { apiClient, TokenManager }

// useful when you want to call the BE from the console and try things out quickly
if (typeof window !== "undefined") {
  ; (window as any).__sdk = sdk
}
